/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Play, Activity, TrendingUp, ArrowRight, ClipboardCheck, History, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { initials } from '@/lib/utils-cat';
import { NoShowRiskBadge } from '@/components/telemedicine/NoShowRiskBadge';
import { LocalAIStatusBadge } from '@/components/clinical/LocalAIStatusBadge';
import { OfflineStatusBadge } from '@/components/clinical/OfflineStatusBadge';
import { URGENCY_META } from './clinicalConstants';

/**
 * Presentational building blocks for the Clinical Record panel.
 * Extracted from ClinicalRecordPanel.jsx (Code Quality refactor).
 */

// Barre decorative dell'onda audio: lista statica con chiavi stabili (no index-as-key)
const WAVE_BARS = ['w1', 'w2', 'w3', 'w4', 'w5', 'w6', 'w7', 'w8'];

/** Barre animate durante la registrazione (solo transform/opacity). */
export const WaveBars = ({ active }) => (
  <div className="flex items-center gap-[3px] h-6" aria-hidden="true">
    {WAVE_BARS.map((k) => (
      <span
        key={k}
        className={cn('block w-[3px] h-full rounded-full bg-destructive', active ? 'wave-bar' : 'scale-y-[0.35] opacity-40')}
      />
    ))}
  </div>
);

export const PanelToolbar = () => (
  <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border/70 bg-card/60" data-testid="clinical-toolbar">
    <div className="font-display font-semibold text-sm flex items-center gap-2">
      <ClipboardCheck className="h-4 w-4 text-primary" /> Cartella clinica
    </div>
    <div className="flex items-center gap-2">
      <OfflineStatusBadge />
      <LocalAIStatusBadge />
    </div>
  </div>
);

/** Header paziente + eventuale triage pre-visita. */
export const ClinicalPatientHeader = ({ appt, onOpenRoom, onImportTriage }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language || 'it';

  const dt = new Date(appt.scheduled_at);
  const urg = URGENCY_META[appt.urgency || 'routine'];
  const now = Date.now();
  const canJoin = (dt.getTime() - 10 * 60000) <= now && now <= (dt.getTime() + 60 * 60000);
  const paid = appt.payment_status === 'paid' || appt.payment_status === 'not_required';
  const triage = appt.triage || {};

  return (
    <>
      <div className="flex items-start gap-3 rounded-xl border border-border/70 bg-card p-3" data-testid="clinical-patient-header">
        <Avatar className="h-12 w-12 ring-2 ring-background">
          <AvatarFallback className="bg-primary/10 text-primary font-bold">{initials(appt.patient_name)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-lg truncate" data-testid="clinical-patient-name">{appt.patient_name}</div>
          <div className="text-xs text-muted-foreground">{dt.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })}{appt.reason ? ` · ${appt.reason}` : ''}</div>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span className={cn('text-[10px] px-2 py-0.5 rounded-full uppercase font-bold', urg.cls)}>{t(urg.label)}</span>
            <span className="text-[10px] text-muted-foreground">{appt.is_free_consultation ? t('telemedFreeTag') : `€ ${appt.visit_price_eur?.toFixed(2)}`}</span>
            <NoShowRiskBadge appointmentId={appt.id} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          {canJoin && paid && appt.status !== 'completed' && appt.status !== 'cancelled' && (
            <Button size="sm" variant="accent" onClick={() => onOpenRoom(appt.id)} className="pulse-glow" data-testid="clinical-join-btn">
              <Play className="h-4 w-4" /> {t('bookInVisitNow')}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/telemedicine/patient/${appt.patient_id}`)} data-testid="clinical-history-btn">
            <TrendingUp className="h-4 w-4" /> Storico <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {(triage.symptoms || triage.severity || triage.duration) && (
        <div className="rounded-xl bg-accent/5 border border-accent/20 p-3 space-y-1.5" data-testid="clinical-triage">
          <div className="text-[10px] uppercase tracking-wider text-accent font-bold flex items-center gap-1"><Activity className="h-3 w-3" /> {t('doctorTriageTitle')}</div>
          {triage.symptoms && <p className="text-sm">{triage.symptoms}</p>}
          <div className="flex gap-4 text-xs text-muted-foreground">
            {triage.duration && <span>Durata: <b className="text-foreground">{triage.duration}</b></span>}
            {triage.severity != null && <span>Severità: <b className="text-foreground">{triage.severity}/10</b></span>}
            {triage.allergies && <span>Allergie: <b className="text-foreground">{triage.allergies}</b></span>}
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onImportTriage(triage)} data-testid="clinical-triage-to-transcript">
            <ClipboardCheck className="h-3.5 w-3.5" /> Importa nel Soggettivo
          </Button>
        </div>
      )}
    </>
  );
};

/** Elenco note precedenti (remote + locali in attesa di sync). */
export const ClinicalNotesHistory = ({ history, loading }) => {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'it';
  return (
    <section className="space-y-2" data-testid="clinical-notes-history">
      <div className="flex items-center gap-2">
        <History className="h-3.5 w-3.5 text-muted-foreground" />
        <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Note precedenti ({history.length})</Label>
      </div>
      {loading ? (
        <div className="space-y-2">{['s1', 's2'].map((k) => <Skeleton key={k} className="h-14 rounded-xl" />)}</div>
      ) : history.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nessuna nota per questo appuntamento.</p>
      ) : (
        <ul className="space-y-2">
          {history.map((n) => (
            <li key={n.id} className="rounded-xl border border-border/70 bg-card p-2.5 text-xs" data-testid={`clinical-note-${n.id}`}>
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-1">
                {new Date(n.created_at).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })}
                {n.local && <Badge variant="outline" className="text-[9px] py-0 h-4 border-warning/50 text-warning">in attesa di sync</Badge>}
              </div>
              <pre className="whitespace-pre-wrap font-sans leading-snug">{n.notes}</pre>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

/** Stato vuoto della cartella: nessun appuntamento selezionato. */
export const EmptyRecordState = () => {
  const { t } = useTranslation();
  return (
    <div className="h-full flex flex-col" data-testid="clinical-record-empty">
      <PanelToolbar />
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center space-y-3 max-w-sm">
          <div className="h-14 w-14 rounded-xl bg-primary/10 text-primary mx-auto flex items-center justify-center"><User className="h-7 w-7" /></div>
          <p className="font-display font-semibold">{t('doctorPanelSelectPatient')}</p>
          <p className="text-sm text-muted-foreground">Seleziona un appuntamento dall'agenda per aprire la cartella clinica, dettare e generare la nota SOAP con l'AI locale.</p>
        </div>
      </div>
    </div>
  );
};
